import { createContext, ReactNode, useContext, useState, useMemo } from "react";

export interface DataCleaningSelection {
  selectedClientIds: number[];
  selectedEntity?: string;
  selectedEntityId?: number;
  selectedFieldId?: number;
}

export interface DataCleaningContextType {
  selection: DataCleaningSelection;
  setSelectedClients: (clientIds: number[]) => void;
  setSelectedEntity: (entityId: number, entityName: string) => void;
  setSelectedField: (fieldId: number) => void;
  resetSelection: () => void;
}

const DataCleaningContext = createContext<DataCleaningContextType | undefined>(
  undefined,
);

interface DataCleaningProviderProps {
  children: ReactNode;
}

const initialSelection: DataCleaningSelection = {
  selectedClientIds: [],
  selectedEntity: undefined,
  selectedEntityId: undefined,
  selectedFieldId: undefined,
};

export const DataCleaningProvider = ({
  children,
}: DataCleaningProviderProps) => {
  const [selection, setSelection] =
    useState<DataCleaningSelection>(initialSelection);

  const setSelectedClients = (clientIds: number[]) => {
    setSelection((prev) => ({
      ...prev,
      selectedClientIds: clientIds,
      // Reset entity and field when clients change
      selectedEntity: undefined,
      selectedEntityId: undefined,
      selectedFieldId: undefined,
    }));
  };

  const setSelectedEntity = (entityId: number, entityName: string) => {
    setSelection((prev) => ({
      ...prev,
      selectedEntity: entityName,
      selectedEntityId: entityId,
      // Reset field when entity changes
      selectedFieldId: undefined,
    }));
  };

  const setSelectedField = (fieldId: number) => {
    setSelection((prev) => ({
      ...prev,
      selectedFieldId: fieldId,
    }));
  };

  const resetSelection = () => {
    setSelection(initialSelection);
  };

  const value = useMemo(
    () => ({
      selection,
      setSelectedClients,
      setSelectedEntity,
      setSelectedField,
      resetSelection,
    }),
    [selection],
  );

  return (
    <DataCleaningContext.Provider value={value}>
      {children}
    </DataCleaningContext.Provider>
  );
};

export const useDataCleaningContext = (): DataCleaningContextType => {
  const context = useContext(DataCleaningContext);
  if (!context) {
    throw new Error(
      "useDataCleaningContext must be used within a DataCleaningProvider",
    );
  }
  return context;
};
